import { LOCALES, pickLocalized, type Locale } from '@mrigl/shared';
import { Alert, Button, SelectField } from '@mrigl/ui';
import {
  BarChart3,
  Banknote,
  Boxes,
  CreditCard,
  ExternalLink,
  FileText,
  FolderTree,
  Globe,
  House,
  Image,
  Layers,
  LayoutDashboard,
  LogOut,
  Menu,
  Package,
  Palette,
  PhoneCall,
  Plug,
  Receipt,
  ScrollText,
  Settings2,
  ShieldAlert,
  ShieldCheck,
  ShoppingBag,
  Store,
  Tag,
  Truck,
  UserRound,
  UsersRound,
  X,
  type LucideIcon,
  MessageSquareText,
  Star,
  MessageSquareHeart,
} from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { NavLink, Navigate, Outlet, useLocation, useMatch } from 'react-router';
import type { MessageKey } from '@mrigl/i18n';
import { useI18n } from './i18n.js';
import { useStore, type StoreDetails } from './pages/catalog/shared.js';
import { FeedbackButton } from './feedback.js';
import { useSession } from './session.js';
import { useSurface } from './surface.js';

const ENDONYM_LANG: Readonly<Record<Locale, string>> = { ar: 'ar', aeb: 'aeb', fr: 'fr', en: 'en' };

export function LanguageSelect() {
  const { locale, setLocale, t } = useI18n();
  return (
    <SelectField
      label={t('admin.language')}
      value={locale}
      onChange={(event) => {
        setLocale(event.target.value as Locale);
      }}
      options={LOCALES.map((value) => ({
        value,
        label: t(`admin.language.${value}`),
        lang: ENDONYM_LANG[value],
      }))}
    />
  );
}

/** The mrigl mark (decorative; the name is always written next to it). */
export function BrandMark({ size = 32 }: { readonly size?: number }) {
  return (
    <svg
      className="admin-mark"
      width={size}
      height={size}
      viewBox="0 0 32 32"
      aria-hidden="true"
      focusable="false"
    >
      <rect width="32" height="32" rx="9" fill="currentColor" />
      <path
        d="M9 22V12.5a2.5 2.5 0 0 1 4.6-1.4L16 14.6l2.4-3.5A2.5 2.5 0 0 1 23 12.5V22"
        fill="none"
        stroke="#fff"
        strokeWidth="2.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** Sign-in and MFA screens: brand panel beside a single card. */
export function AuthLayout({ children }: { readonly children: ReactNode }) {
  const { t } = useI18n();
  const surface = useSurface();
  return (
    <main className="admin-auth">
      <section className="admin-auth__brand" aria-hidden="true">
        <div className="admin-auth__logo">
          <BrandMark size={40} />
          <span>{t('brand.name')}</span>
        </div>
        {surface === 'console' ? (
          <>
            <p className="admin-auth__tagline">{t('admin.console.tagline')}</p>
            <ul className="admin-auth__points">
              <li>{t('admin.console.note')}</li>
            </ul>
          </>
        ) : (
          <>
            <p className="admin-auth__tagline">{t('admin.auth.tagline')}</p>
            <ul className="admin-auth__points">
              <li>{t('admin.auth.point.orders')}</li>
              <li>{t('admin.auth.point.languages')}</li>
              <li>{t('admin.auth.point.secure')}</li>
            </ul>
          </>
        )}
      </section>
      <div className="admin-auth__panel">
        <div className="admin-auth__mobile-logo">
          <BrandMark size={32} />
          <span>{t('brand.name')}</span>
        </div>
        <div className="mrigl-stack admin-auth__inner">
          {children}
          <LanguageSelect />
        </div>
      </div>
    </main>
  );
}

export function Loading() {
  const { t } = useI18n();
  return (
    <p className="mrigl-muted admin-loading" role="status">
      <span className="admin-spinner" aria-hidden="true" />
      {t('admin.loading')}
    </p>
  );
}

interface NavItem {
  readonly to: string;
  readonly label: MessageKey;
  readonly icon: LucideIcon;
  readonly permission?: string;
  readonly end?: boolean;
}

interface NavGroup {
  readonly title: MessageKey | null;
  readonly items: readonly NavItem[];
}

/** The store's sections, grouped as merchants think about them. */
function storeGroups(storeId: string): readonly NavGroup[] {
  const base = `/stores/${storeId}`;
  return [
    {
      title: null,
      items: [{ to: base, label: 'admin.nav.storeHome', icon: LayoutDashboard, end: true }],
    },
    {
      title: 'admin.nav.group.sales',
      items: [
        {
          to: `${base}/orders`,
          label: 'admin.order.title',
          icon: ShoppingBag,
          permission: 'orders.read',
        },
        {
          to: `${base}/analytics`,
          label: 'admin.analytics.title',
          icon: BarChart3,
          permission: 'analytics.read',
        },
        {
          to: `${base}/reviews`,
          label: 'admin.reviews.title',
          icon: Star,
          permission: 'reviews.read',
        },
        {
          to: `${base}/comments`,
          label: 'admin.comments.title',
          icon: MessageSquareText,
          permission: 'reviews.read',
        },
        {
          to: `${base}/discounts`,
          label: 'admin.discount.title',
          icon: Tag,
          permission: 'discounts.manage',
        },
        {
          to: `${base}/privacy`,
          label: 'admin.privacy.title',
          icon: UsersRound,
          permission: 'privacy.manage',
        },
      ],
    },
    {
      title: 'admin.nav.group.catalog',
      items: [
        { to: `${base}/products`, label: 'admin.catalog.products', icon: Package },
        { to: `${base}/collections`, label: 'admin.catalog.collections', icon: FolderTree },
        { to: `${base}/inventory`, label: 'admin.inventory.title', icon: Boxes },
        { to: `${base}/media`, label: 'admin.media.title', icon: Image },
      ],
    },
    {
      title: 'admin.nav.group.online',
      items: [
        { to: `${base}/theme`, label: 'admin.theme.title', icon: Palette },
        { to: `${base}/domains`, label: 'admin.domains.title', icon: Globe },
      ],
    },
    {
      title: 'admin.nav.group.fulfilment',
      items: [
        { to: `${base}/checkout`, label: 'admin.checkout.title', icon: CreditCard },
        { to: `${base}/cod`, label: 'admin.cod.title', icon: PhoneCall, permission: 'orders.read' },
        {
          to: `${base}/carriers`,
          label: 'admin.carriers.title',
          icon: Truck,
          permission: 'orders.read',
        },
        {
          to: `${base}/cash`,
          label: 'admin.payments.title',
          icon: Banknote,
          permission: 'orders.read',
        },
      ],
    },
    {
      title: 'admin.nav.group.settings',
      items: [
        { to: `${base}/invoicing`, label: 'admin.invoice.title', icon: FileText },
        {
          to: `${base}/billing`,
          label: 'admin.billing.title',
          icon: Receipt,
          permission: 'billing.read',
        },
        {
          to: `${base}/integrations`,
          label: 'admin.integrations.title',
          icon: Plug,
          permission: 'apps.manage',
        },
        {
          to: `${base}/audit`,
          label: 'admin.home.auditLog',
          icon: ScrollText,
          permission: 'audit.read',
        },
      ],
    },
  ];
}

function NavEntry({ item }: { readonly item: NavItem }) {
  const { t } = useI18n();
  const Icon = item.icon;
  return (
    <li>
      <NavLink className="admin-navlink" to={item.to} end={item.end ?? false}>
        <Icon className="admin-navlink__icon" size={18} aria-hidden="true" />
        <span>{t(item.label)}</span>
      </NavLink>
    </li>
  );
}

function StoreNav({ store }: { readonly store: StoreDetails }) {
  const { t } = useI18n();
  return (
    <nav className="admin-sidebar__section" aria-label={t('admin.nav.store')}>
      {storeGroups(store.storeId).map((group) => {
        const items = group.items.filter(
          (item) => item.permission === undefined || store.permissions.includes(item.permission),
        );
        if (items.length === 0) return null;
        return (
          <div key={group.title ?? 'home'} className="admin-navgroup">
            {group.title === null ? null : (
              <p className="admin-navgroup__title">{t(group.title)}</p>
            )}
            <ul className="admin-navlist">
              {items.map((item) => (
                <NavEntry key={item.to} item={item} />
              ))}
            </ul>
          </div>
        );
      })}
    </nav>
  );
}

/** Store chip in the sidebar: name, address and a link to the live storefront. */
function StoreChip({ store }: { readonly store: StoreDetails }) {
  const { t, locale } = useI18n();
  const name = pickLocalized(store.name, locale, store.defaultLocale) ?? store.slug;
  const host = store.hosts.find((item) => item.canonical)?.host ?? store.hosts[0]?.host ?? null;
  return (
    <div className="admin-storechip">
      <span className="admin-storechip__avatar" aria-hidden="true">
        {name.trim().charAt(0).toUpperCase()}
      </span>
      <span className="admin-storechip__text">
        <span className="admin-storechip__name" dir="auto">
          {name}
        </span>
        {host === null ? null : (
          <a
            className="admin-storechip__host mrigl-ltr"
            href={`https://${host}`}
            target="_blank"
            rel="noreferrer"
          >
            {t('admin.nav.viewStore')}
            <ExternalLink size={12} aria-hidden="true" />
          </a>
        )}
      </span>
    </div>
  );
}

/** Application frame for signed-in merchant pages; redirects according to the session state. */
export function SignedInLayout() {
  const { t } = useI18n();
  const match = useMatch('/stores/:storeId/*');
  const storeId = match?.params.storeId;
  const inStore = storeId !== undefined && storeId !== 'new';
  return (
    <AppFrame>
      {inStore ? <StoreSidebar storeId={storeId} /> : null}
      <nav
        className={`admin-sidebar__section${inStore ? ' admin-sidebar__footer' : ''}`}
        aria-label={t('admin.nav.label')}
      >
        <ul className="admin-navlist admin-nav">
          <NavEntry item={{ to: '/', label: 'admin.nav.home', icon: House, end: true }} />
          <NavEntry item={{ to: '/account', label: 'admin.nav.account', icon: UserRound }} />
          <NavEntry item={{ to: '/security', label: 'admin.nav.security', icon: ShieldCheck }} />
        </ul>
      </nav>
    </AppFrame>
  );
}

/** Sidebar entries of the operator console (ADR 0025). */
export function ConsoleNav() {
  const { t } = useI18n();
  return (
    <nav className="admin-sidebar__section admin-console-nav" aria-label={t('admin.nav.label')}>
      <div className="admin-navgroup">
        <ul className="admin-navlist admin-nav">
          <NavEntry item={{ to: '/', label: 'admin.operator.stores', icon: Store, end: true }} />
          <NavEntry
            item={{ to: '/merchants', label: 'admin.operator.merchants', icon: UsersRound }}
          />
          <NavEntry item={{ to: '/plans', label: 'admin.operator.plans', icon: Layers }} />
          <NavEntry item={{ to: '/settings', label: 'admin.operator.settings', icon: Settings2 }} />
          <NavEntry item={{ to: '/landing', label: 'admin.landing.title', icon: Globe }} />
          <NavEntry
            item={{
              to: '/feedback',
              label: 'admin.operator.feedback.title',
              icon: MessageSquareHeart,
            }}
          />
          <NavEntry
            item={{ to: '/listings', label: 'admin.operator.watch.title', icon: ShieldAlert }}
          />
        </ul>
      </div>
      <div className="admin-navgroup">
        <ul className="admin-navlist admin-nav">
          <NavEntry item={{ to: '/account', label: 'admin.nav.account', icon: UserRound }} />
          <NavEntry item={{ to: '/security', label: 'admin.nav.security', icon: ShieldCheck }} />
        </ul>
      </div>
    </nav>
  );
}

/**
 * Frame shared by the merchant admin and the operator console: session gate (sign-in, MFA),
 * sidebar with the given navigation, header with the account and sign-out.
 */
export function AppFrame({ children }: { readonly children: ReactNode }) {
  const { state, signOut } = useSession();
  const { t } = useI18n();
  const surface = useSurface();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  // Close the mobile menu after navigating.
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  if (state.kind === 'loading') return <Loading />;
  if (state.kind === 'signed-out' || state.kind === 'mfa-pending') {
    writeReturnTo(location.pathname + location.search);
  }
  if (state.kind === 'signed-out') return <Navigate to="/sign-in" replace />;
  if (state.kind === 'mfa-pending') return <Navigate to="/mfa" replace />;
  if (state.kind === 'error') {
    return (
      <main className="admin-main">
        <Alert tone="error">{t('admin.error.generic')}</Alert>
      </main>
    );
  }

  const me = state.me;
  const who = me.user.displayName ?? me.user.email ?? '';
  return (
    <div className="admin-shell" data-nav-open={open ? 'true' : undefined}>
      <a className="admin-skip" href="#admin-content">
        {t('admin.nav.skip')}
      </a>
      <aside className="admin-sidebar" id="admin-sidebar">
        <div className="admin-sidebar__brand">
          <BrandMark size={28} />
          <span className="admin-brand">{t('brand.name')}</span>
          {surface === 'console' ? (
            <span className="admin-status">{t('admin.nav.operator')}</span>
          ) : null}
          <button
            type="button"
            className="admin-iconbutton admin-sidebar__close"
            aria-label={t('admin.nav.closeMenu')}
            onClick={() => {
              setOpen(false);
            }}
          >
            <X size={18} aria-hidden="true" />
          </button>
        </div>
        {children}
      </aside>
      <div
        className="admin-scrim"
        aria-hidden="true"
        onClick={() => {
          setOpen(false);
        }}
      />
      <div className="admin-frame">
        <header className="admin-header">
          <button
            type="button"
            className="admin-iconbutton admin-header__menu"
            aria-label={t('admin.nav.menu')}
            aria-expanded={open}
            aria-controls="admin-sidebar"
            onClick={() => {
              setOpen(!open);
            }}
          >
            <Menu size={20} aria-hidden="true" />
          </button>
          <span className="admin-header__spacer" />
          {surface === 'admin' ? <FeedbackButton /> : null}
          <span className="admin-user" title={me.user.email ?? undefined}>
            <span className="admin-user__avatar" aria-hidden="true">
              {who.trim().charAt(0).toUpperCase()}
            </span>
            <span className="admin-user__name" dir="auto">
              {who}
            </span>
          </span>
          <Button
            variant="secondary"
            onClick={() => {
              void signOut();
            }}
          >
            <LogOut size={16} aria-hidden="true" />
            <span className="admin-header__signout-label">{t('admin.nav.signOut')}</span>
          </Button>
        </header>
        <main className="admin-main" id="admin-content" tabIndex={-1}>
          <Outlet />
        </main>
      </div>
    </div>
  );
}

function StoreSidebar({ storeId }: { readonly storeId: string }) {
  const { t } = useI18n();
  const { store } = useStore(storeId, t);
  if (store === null) return null;
  return (
    <>
      <StoreChip store={store} />
      <StoreNav store={store} />
    </>
  );
}

const RETURN_TO_KEY = 'mrigl.returnTo';

/** Only in-app absolute paths are kept (never another origin: no open redirect). */
function isInternalPath(path: string): boolean {
  return path.startsWith('/') && !path.startsWith('//') && !path.startsWith('/\\');
}

export function writeReturnTo(path: string): void {
  if (path === '/' || !isInternalPath(path)) return;
  try {
    globalThis.sessionStorage.setItem(RETURN_TO_KEY, path);
  } catch {
    // Without storage the user lands on the home page after sign-in.
  }
}

/** Where to go after sign-in completes (and forgets it). */
export function consumeReturnTo(): string {
  try {
    const path = globalThis.sessionStorage.getItem(RETURN_TO_KEY);
    globalThis.sessionStorage.removeItem(RETURN_TO_KEY);
    return path !== null && isInternalPath(path) ? path : '/';
  } catch {
    return '/';
  }
}
